/**
 * The one model editor.
 *
 * An ordered list of routes, each Machine → Harness → Model → Thinking, narrowed left to
 * right by `lib/route-list.ts`, with move, remove and "+ Add route". The project execution
 * page, the ticket Overrides tab, the Sidecars page and the Nightshift Models page all render
 * this; what differs between them is only what the list is *for*, which the host says in the
 * heading and help it passes in. A surface that needs a model must use this rather than a
 * text field, so a slug can be chosen but not mistyped.
 */
import type { ReactNode } from 'react';

import type { UiCatalogueModel } from '@nightshift/ui-types';

import {
  duplicateRows,
  effortsFor,
  findMachine,
  harnessInstalledOn,
  harnessLabel,
  harnessesFor,
  machineOptionLabel,
  modelsFor,
  moveRow,
  newRow,
  rowAfterHarnessChange,
  rowAfterMachineChange,
  rowAfterModelChange,
  rowProblem,
  type RouteMachine,
  type RouteRow,
} from '../lib/route-list.ts';

export type { RouteMachine, RouteRow } from '../lib/route-list.ts';

const ANY_MACHINE = '';

export interface RouteListEditorProps {
  rows: RouteRow[];
  onChange: (rows: RouteRow[]) => void;
  machines: readonly RouteMachine[];
  models: readonly UiCatalogueModel[];
  disabled?: boolean;
  /** Accessible prefix for every control in the list: "Footprint route 2 machine". */
  label: string;
  /** What a row with no machine means here. */
  anyMachineLabel?: string;
  /** Shown instead of the table while the list is empty. */
  emptyText?: ReactNode;
  addLabel?: string;
  /** Extra copy under the table, such as what an empty list inherits. */
  footer?: ReactNode;
}

/** One cell of a route row: the row, where its changes go, and the lists that narrow it. */
interface PickerProps {
  row: RouteRow;
  onChange: (row: RouteRow) => void;
  machines: readonly RouteMachine[];
  models: readonly UiCatalogueModel[];
  disabled?: boolean;
  /** The control's accessible name. */
  label: string;
}

/**
 * The Harness, Model and Thinking dropdowns of one row, usable on their own. The route list
 * renders them per row; a single-route chooser (the routes overview's "replace with") renders
 * the same three, so a target is narrowed exactly as a stored row is.
 */
/** Why a stored harness is not among the choices: absent from the machine, or present without models. */
function missingHarnessWord(row: RouteRow, machines: readonly RouteMachine[]): string {
  if (!row.machine) return 'unavailable';
  return harnessInstalledOn(row.machine, row.harness, machines) ? 'no models' : 'not installed';
}

export function HarnessPicker({ row, onChange, machines, models, disabled = false, label }: PickerProps) {
  const harnesses = harnessesFor(row.machine, machines, models);
  return (
    <select
      className="input"
      aria-label={label}
      value={row.harness}
      disabled={disabled}
      onChange={(event) => onChange(rowAfterHarnessChange(row, event.target.value, models))}
    >
      {harnesses.includes(row.harness) ? null : (
        <option value={row.harness}>
          {harnessLabel(row.harness)} ({missingHarnessWord(row, machines)})
        </option>
      )}
      {harnesses.map((harness) => (
        <option key={harness} value={harness}>{harnessLabel(harness)}</option>
      ))}
    </select>
  );
}

export function ModelPicker({ row, onChange, models, disabled = false, label }: PickerProps) {
  const options = modelsFor(row.machine, row.harness, models);
  return (
    <select
      className="input mono"
      aria-label={label}
      value={row.model}
      disabled={disabled}
      onChange={(event) => onChange(rowAfterModelChange(row, event.target.value, models))}
    >
      {options.some((model) => model.id === row.model) ? null : (
        <option value={row.model} disabled>{row.model || 'Select a model'} (unavailable)</option>
      )}
      {options.map((model) => (
        <option key={model.id} value={model.id}>
          {model.id}
          {model.badge ? ` · ${model.badge}` : ''}
        </option>
      ))}
    </select>
  );
}

export function ThinkingPicker({ row, onChange, models, disabled = false, label }: PickerProps) {
  const efforts = effortsFor(row.harness, row.model, models);
  return (
    <select
      className="input"
      aria-label={label}
      value={row.thinking ?? ''}
      disabled={disabled || (efforts.length === 0 && !row.thinking)}
      onChange={(event) => onChange({ ...row, thinking: event.target.value || null })}
    >
      <option value="">none</option>
      {row.thinking && !efforts.includes(row.thinking) ? (
        <option value={row.thinking}>{row.thinking} (unavailable)</option>
      ) : null}
      {efforts.map((effort) => (
        <option key={effort} value={effort}>{effort}</option>
      ))}
    </select>
  );
}

export function RouteListEditor({
  rows,
  onChange,
  machines,
  models,
  disabled = false,
  label,
  anyMachineLabel = 'Any machine',
  emptyText = 'No routes. Add one to start the list.',
  addLabel = '+ Add route',
  footer,
}: RouteListEditorProps) {
  const duplicates = duplicateRows(rows);
  const update = (index: number, next: RouteRow) => onChange(rows.map((row, i) => (i === index ? next : row)));
  const add = () => {
    const row = newRow(rows, machines, models);
    if (row) onChange([...rows, row]);
  };
  const noCatalogue = models.length === 0;

  return (
    <div className="route-list">
      {rows.length === 0 ? (
        <p className="cfg-help route-list-empty">{emptyText}</p>
      ) : (
        <table className="routes-table">
          <thead>
            <tr>
              <th>Machine</th>
              <th>Harness</th>
              <th>Model</th>
              <th>Thinking</th>
              <th aria-label="Order" />
              <th aria-label="Remove" />
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => {
              const prefix = `${label} ${index + 1}`;
              const picker = { row, onChange: (next: RouteRow) => update(index, next), machines, models, disabled };
              const problem = rowProblem(row, machines, models);
              const duplicate = duplicates.has(index);
              const machineKnown = row.machine === null || findMachine(machines, row.machine) !== undefined;
              return (
                <tr key={index} className={duplicate || problem ? 'routes-row-warn' : undefined}>
                  <td>
                    <select
                      className="input"
                      aria-label={`${prefix} machine`}
                      value={row.machine ?? ANY_MACHINE}
                      disabled={disabled}
                      onChange={(event) =>
                        update(index, rowAfterMachineChange(row, event.target.value || null, machines, models))
                      }
                    >
                      <option value={ANY_MACHINE}>{anyMachineLabel}</option>
                      {machineKnown ? null : <option value={row.machine ?? ''}>{row.machine} (not registered)</option>}
                      {machines.map((machine) => (
                        <option key={machine.name} value={machine.name}>
                          {machineOptionLabel(machine)}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <HarnessPicker {...picker} label={`${prefix} harness`} />
                  </td>
                  <td>
                    <ModelPicker {...picker} label={`${prefix} model`} />
                  </td>
                  <td>
                    <ThinkingPicker {...picker} label={`${prefix} thinking`} />
                  </td>
                  <td className="routes-order">
                    <button
                      type="button"
                      className="btn btn-icon"
                      aria-label={`Move ${prefix} earlier`}
                      disabled={disabled || index === 0}
                      onClick={() => onChange(moveRow(rows, index, -1))}
                    >
                      ↑
                    </button>
                    <button
                      type="button"
                      className="btn btn-icon"
                      aria-label={`Move ${prefix} later`}
                      disabled={disabled || index === rows.length - 1}
                      onClick={() => onChange(moveRow(rows, index, 1))}
                    >
                      ↓
                    </button>
                  </td>
                  <td>
                    <button
                      type="button"
                      className="btn btn-icon"
                      aria-label={`Remove ${prefix}`}
                      disabled={disabled}
                      onClick={() => onChange(rows.filter((_, i) => i !== index))}
                    >
                      −
                    </button>
                  </td>
                  {problem || duplicate ? (
                    <td className="routes-row-note" role="note">
                      {duplicate ? 'Repeats an earlier row.' : problem}
                    </td>
                  ) : null}
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      <div className="route-list-foot">
        <button type="button" className="btn btn-sm" disabled={disabled || noCatalogue} onClick={add}>
          {addLabel}
        </button>
        {noCatalogue ? <span className="cfg-help">No models are available to choose from yet.</span> : null}
        {footer ? <span className="cfg-help">{footer}</span> : null}
      </div>
    </div>
  );
}
